/**
 * Deterministic checksum over the frozen word list.
 *
 * Spec section 9, sheet 3 "Meta": the export carries a word-list checksum so an
 * operator can tell whether the export was produced against the same question
 * set as the workbook in front of them. If it differs, the export must not be
 * pasted.
 *
 * FNV-1a, run twice with different offset bases, so the result is:
 *   - synchronous, which WebCrypto SHA-256 is not, and this value is needed
 *     inline in a spreadsheet cell and in test assertions
 *   - dependency-free and identical in Workers, browsers, and Node
 *   - short enough for a human to compare two strings by eye
 *
 * This detects change. It is not a security primitive and is not used as one.
 */

const FNV_PRIME = 0x01000193;
const FNV_OFFSET_A = 0x811c9dc5;
const FNV_OFFSET_B = 0x9dc5811c;

function fnv1a(bytes: Uint8Array, offsetBasis: number): number {
  let hash = offsetBasis;
  for (const byte of bytes) {
    hash ^= byte;
    hash = Math.imul(hash, FNV_PRIME) >>> 0;
  }
  return hash >>> 0;
}

function hex8(n: number): string {
  return n.toString(16).padStart(8, '0');
}

/**
 * Stable serialisation of the word list. Every field that could change the
 * meaning of an export is included, so any drift changes the checksum.
 */
export function canonicalWordList(
  instrumentVersion: string,
  questions: readonly {
    readonly qsPosition: number;
    readonly sourceQ46: number;
    readonly words: readonly [string, string, string];
    readonly wordNums: readonly [number, number, number];
  }[],
): string {
  const lines = questions.map(
    (q) =>
      `${q.qsPosition}|${q.sourceQ46}|${q.words[0]}|${q.words[1]}|${q.words[2]}|` +
      `${q.wordNums[0]}|${q.wordNums[1]}|${q.wordNums[2]}`,
  );
  return [instrumentVersion, ...lines].join('\n');
}

/** `QS17-xxxxxxxx-xxxxxxxx`. */
export function wordListChecksum(canonical: string): string {
  const bytes = new TextEncoder().encode(canonical);
  return `QS17-${hex8(fnv1a(bytes, FNV_OFFSET_A))}-${hex8(fnv1a(bytes, FNV_OFFSET_B))}`;
}
